/* prettier-ignore-file */
/**
 * The app's button primitive.
 *
 * Before this, every button was an ad-hoc TouchableOpacity + Text pair with its own
 * hardcoded background, which is why the UI read as "multicoloured": a screen could
 * show a blue, a green, an orange and a red button side by side with no consistent
 * meaning.
 *
 * The rule here: exactly ONE primary action per view. Everything else is
 * `secondary` or `ghost`. `danger` is reserved for destructive, irreversible
 * actions — never for "cancel". Colour carries meaning; it is not decoration.
 *
 * HOVER: `dataSet={{hover:"lift"}}` renders data-hover="lift", which the global rule
 * in public/index.html animates. That indirection exists because react-native-web's
 * StyleSheet cannot express :hover, and TouchableOpacity emits no role="button" to
 * hang a global selector off.
 */

import React from "react";
import { Text, StyleSheet, TouchableOpacity, ActivityIndicator, View } from "react-native";
import { colors, radius, spacing, elevation, sheen } from "../../theme";

/**
 * Solid buttons get a tinted ambient glow in their own hue rather than a neutral grey
 * shadow — a teal button casts teal light. Plus an inset top highlight so the fill has
 * a subtle sheen instead of reading as a flat rectangle.
 */
const solidShadow = (rgb) =>
  `0 1px 2px rgba(15, 26, 25, 0.10), 0 4px 12px rgba(${rgb}, 0.28), ${sheen.onDark}`;

const Button = ({
  title,
  onPress,
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  fullWidth = false,
  icon,
  style,
}) => {
  const isDisabled = disabled || loading;
  const v = VARIANTS[variant] || VARIANTS.primary;

  return (
    <TouchableOpacity
      dataSet={{ hover: "lift" }}
      style={[
        styles.base,
        size === "sm" ? styles.sizeSm : styles.sizeMd,
        {
          backgroundColor: isDisabled && v.disabledBg ? v.disabledBg : v.bg,
          borderColor: v.border || "transparent",
          borderWidth: v.border ? 1 : 0,
        },
        v.shadow && !isDisabled ? { boxShadow: v.shadow } : null,
        fullWidth && styles.fullWidth,
        isDisabled && styles.disabled,
        style,
      ]}
      onPress={onPress}
      disabled={isDisabled}
      activeOpacity={0.9}
    >
      {loading ? (
        <ActivityIndicator size="small" color={v.fg} />
      ) : (
        <View style={styles.content}>
          {icon ? <Text style={[styles.icon, { color: isDisabled ? colors.ink400 : v.fg }]}>{icon}</Text> : null}
          <Text
            style={[
              styles.label,
              size === "sm" ? styles.labelSm : styles.labelMd,
              { color: isDisabled ? colors.ink400 : v.fg },
            ]}
            numberOfLines={1}
          >
            {title}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

const VARIANTS = {
  primary: {
    bg: colors.primary,
    fg: colors.surface,
    border: null,
    disabledBg: colors.disabled,
    shadow: solidShadow("14, 124, 107"),
  },
  secondary: {
    bg: colors.surface,
    fg: colors.ink700,
    border: colors.border,
    disabledBg: colors.surfaceMuted,
    shadow: `${elevation.sm}, ${sheen.onLight}`,
  },
  ghost: { bg: "transparent", fg: colors.primary, border: null, disabledBg: "transparent", shadow: null },
  danger: {
    bg: colors.danger,
    fg: colors.surface,
    border: null,
    disabledBg: colors.disabled,
    shadow: solidShadow("196, 69, 61"),
  },
};

const styles = StyleSheet.create({
  base: {
    borderRadius: radius.md,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  // Slightly tighter vertical rhythm than the old 15px pads, with roomier
  // horizontal padding — reads more deliberate at the same visual weight.
  sizeMd: { paddingVertical: 12, paddingHorizontal: spacing.xl, minHeight: 44 },
  sizeSm: { paddingVertical: 8, paddingHorizontal: spacing.lg, minHeight: 34 },
  fullWidth: { alignSelf: "stretch" },
  disabled: { cursor: "default" },
  content: { flexDirection: "row", alignItems: "center", justifyContent: "center" },
  icon: { fontSize: 15, marginRight: 7 },
  label: { fontWeight: "600", letterSpacing: 0.2 },
  labelMd: { fontSize: 15 },
  labelSm: { fontSize: 13 },
});

export default Button;
